/**
 * Temporary utility to make all users administrators
 * WARNING: This gives admin access to ALL users in the system
 * Use with caution and revert when done testing
 */

import UserManagementUtils from './userManagement.js';

/**
 * Make all users in the system administrators
 * @param {string} adminUserId - The admin user ID making the change
 * @param {string} adminEmail - The admin user email making the change
 * @returns {Object} - Result object with success status and details
 */
export async function makeAllUsersAdmin(adminUserId, adminEmail) {
  try {
    console.log('Starting to make all users administrators...');
    
    // Get all users
    const allUsers = await UserManagementUtils.getAllUsers();
    console.log(`Found ${allUsers.length} users in the system`);
    
    const results = {
      success: true,
      totalUsers: allUsers.length,
      updatedUsers: [],
      alreadyAdmin: [],
      errors: []
    };
    
    // Process each user
    for (const user of allUsers) {
      try {
        if (user.role === 'admin') {
          results.alreadyAdmin.push({
            id: user.id,
            email: user.email,
            name: `${user.firstName} ${user.lastName}`
          });
          console.log(`User ${user.email} is already an admin`);
        } else {
          // Make user admin
          await UserManagementUtils.updateUserRole(user.id, 'admin', adminUserId, adminEmail);
          results.updatedUsers.push({
            id: user.id,
            email: user.email,
            name: `${user.firstName} ${user.lastName}`,
            previousRole: user.role
          });
          console.log(`Updated ${user.email} to admin (was ${user.role})`);
        }
      } catch (error) {
        console.error(`Error updating user ${user.email}:`, error);
        results.errors.push({
          id: user.id,
          email: user.email,
          error: error.message
        });
      }
    }
    
    console.log('Make all users admin completed:');
    console.log(`- Total users: ${results.totalUsers}`);
    console.log(`- Updated to admin: ${results.updatedUsers.length}`);
    console.log(`- Already admin: ${results.alreadyAdmin.length}`);
    console.log(`- Errors: ${results.errors.length}`);
    
    return results;
  } catch (error) {
    console.error('Error making all users admin:', error);
    throw error;
  }
}

/**
 * Revert all users back to organizer role (except the original admin)
 * @param {string} originalAdminUserId - The original admin user ID to keep as admin
 * @param {string} adminUserId - The admin user ID making the change
 * @param {string} adminEmail - The admin user email making the change
 * @returns {Object} - Result object with success status and details
 */
export async function revertAllUsersToOrganizer(originalAdminUserId, adminUserId, adminEmail) {
  try {
    console.log('Starting to revert all users back to organizer...');
    
    // Get all users
    const allUsers = await UserManagementUtils.getAllUsers();
    console.log(`Found ${allUsers.length} users in the system`);
    
    const results = {
      success: true,
      totalUsers: allUsers.length,
      revertedUsers: [],
      keptAsAdmin: [],
      errors: []
    };
    
    // Process each user
    for (const user of allUsers) {
      try {
        if (user.id === originalAdminUserId) {
          // Keep the original admin as admin
          results.keptAsAdmin.push({
            id: user.id,
            email: user.email,
            name: `${user.firstName} ${user.lastName}`
          });
          console.log(`Keeping ${user.email} as admin (original admin)`);
        } else if (user.role === 'admin') {
          // Revert to organizer
          await UserManagementUtils.updateUserRole(user.id, 'organizer', adminUserId, adminEmail);
          results.revertedUsers.push({
            id: user.id,
            email: user.email,
            name: `${user.firstName} ${user.lastName}`
          });
          console.log(`Reverted ${user.email} to organizer`);
        } else {
          console.log(`User ${user.email} is already ${user.role}`);
        }
      } catch (error) {
        console.error(`Error updating user ${user.email}:`, error);
        results.errors.push({
          id: user.id,
          email: user.email,
          error: error.message
        });
      }
    }
    
    console.log('Revert all users completed:');
    console.log(`- Total users: ${results.totalUsers}`);
    console.log(`- Reverted to organizer: ${results.revertedUsers.length}`);
    console.log(`- Kept as admin: ${results.keptAsAdmin.length}`);
    console.log(`- Errors: ${results.errors.length}`);
    
    return results;
  } catch (error) {
    console.error('Error reverting all users:', error);
    throw error;
  }
}

/**
 * Get current admin status of all users
 * @returns {Object} - Summary of user roles
 */
export async function getUsersAdminStatus() {
  try {
    const allUsers = await UserManagementUtils.getAllUsers();
    
    const status = {
      totalUsers: allUsers.length,
      admins: [],
      organizers: [],
      other: []
    };
    
    allUsers.forEach(user => {
      const userInfo = {
        id: user.id,
        email: user.email,
        name: `${user.firstName} ${user.lastName}`,
        role: user.role
      };
      
      if (user.role === 'admin') {
        status.admins.push(userInfo);
      } else if (user.role === 'organizer') {
        status.organizers.push(userInfo);
      } else {
        status.other.push(userInfo);
      }
    });
    
    console.log('Current user admin status:');
    console.log(`- Total users: ${status.totalUsers}`);
    console.log(`- Admins: ${status.admins.length}`);
    console.log(`- Organizers: ${status.organizers.length}`);
    console.log(`- Other roles: ${status.other.length}`);
    
    return status;
  } catch (error) {
    console.error('Error getting users admin status:', error);
    throw error;
  }
}

// Make functions available globally for console use
if (typeof window !== 'undefined') {
  window.makeAllUsersAdmin = makeAllUsersAdmin;
  window.revertAllUsersToOrganizer = revertAllUsersToOrganizer;
  window.getUsersAdminStatus = getUsersAdminStatus;
  console.log('Temporary admin utilities loaded. Use:');
  console.log('- makeAllUsersAdmin(userId, email)');
  console.log('- revertAllUsersToOrganizer(originalAdminId, userId, email)');
  console.log('- getUsersAdminStatus()');
}
